import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOSchema from '@/components/feature/SEOSchema';
import { categoriesData, categoryArticlesMap } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'training');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categoriesData.find((c) => c.slug === activeSlug) || categoriesData[0];
  const articles = categoryArticlesMap[activeSlug] || [];

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="Organization" />
      <SEOSchema
        type="WebPage"
        data={{
          name: `${currentCategory.name} - 云开体育`,
          description: currentCategory.description,
        }}
      />

      <Navbar />
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: currentCategory.name },
        ]}
      />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-2">
          {currentCategory.name}
        </h1>
        <p className="text-foreground-600 text-base mb-8 max-w-2xl">{currentCategory.description}</p>

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
          {categoriesData.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-xs`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        {articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 transition-all hover:scale-[1.01] cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2">{article.title}</h2>
                  <p className="text-foreground-500 text-sm line-clamp-2 mb-3">{article.summary}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i> {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i> {article.date}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-article-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-base">该分类暂未发布文章，敬请期待</p>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}